import { useEffect, useRef, useState } from 'react';
import { ChevronDown, LogOut, Settings, UserCircle2 } from 'lucide-react';
import { resolvePortalAssetUrl } from '../../services/eportalAuthService';

function ProfileMenuButton({ avatar, label = 'Account', onLogout, onOpenSettings }) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const resolvedAvatar = resolvePortalAssetUrl(avatar);

  useEffect(() => {
    function handleClickOutside(event) {
      if (!dropdownRef.current?.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  function closeAndRun(action) {
    setIsDropdownOpen(false);

    if (typeof action === 'function') {
      action();
    }
  }

  return (
    <div className="relative shrink-0" ref={dropdownRef}>
      <button
        aria-expanded={isDropdownOpen}
        aria-haspopup="menu"
        className="flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-white py-1 pl-1 pr-2 text-left text-[var(--theme-text-subtle)] shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.06)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-surface-soft)] sm:min-h-[42px] sm:w-auto sm:gap-2.5 sm:rounded-[14px] sm:pl-1.5 sm:pr-3.5"
        onClick={() => setIsDropdownOpen((currentValue) => !currentValue)}
        type="button"
      >
        {resolvedAvatar ? (
          <img
            alt={`${label} avatar`}
            className="h-7 w-7 shrink-0 rounded-full border border-[var(--theme-border-soft)] object-cover sm:h-8 sm:w-8 sm:rounded-[10px]"
            src={resolvedAvatar}
          />
        ) : (
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-text-subtle)] sm:h-8 sm:w-8 sm:rounded-[10px]">
            <UserCircle2 className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
          </span>
        )}
        <strong className="hidden max-w-[150px] truncate text-[0.88rem] font-semibold text-[var(--theme-text-strong)] sm:block">
          {label}
        </strong>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-[var(--theme-text-subtle)] transition-transform ${
            isDropdownOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isDropdownOpen ? (
        <div
          aria-label="Account menu"
          className="absolute right-0 top-[calc(100%+10px)] z-20 w-[220px] rounded-2xl border border-[var(--theme-border)] bg-white p-2 shadow-[0_22px_52px_rgba(var(--theme-shadow-rgb),0.14)] sm:w-[240px]"
          role="menu"
        >
          <button
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-[var(--theme-text)] transition hover:bg-[var(--theme-surface-soft)]"
            onClick={() => closeAndRun(onOpenSettings)}
            role="menuitem"
            type="button"
          >
            <Settings className="h-4 w-4 shrink-0 text-[var(--theme-text-subtle)]" />
            <span className="truncate">Settings</span>
          </button>
          <button
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-[var(--theme-text)] transition hover:bg-[var(--theme-surface-soft)]"
            onClick={() => closeAndRun(onLogout)}
            role="menuitem"
            type="button"
          >
            <LogOut className="h-4 w-4 shrink-0 text-[var(--theme-text-subtle)]" />
            <span className="truncate">Logout</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default ProfileMenuButton;
